import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import { useCallback, useState } from 'react';

/** A column entry for Cloudscape's `columnDisplay` (visibility + order). */
export interface ContentDisplayItem {
  id: string;
  visible: boolean;
}

export interface TablePrefs {
  pageSize: number;
  wrapLines: boolean;
  contentDisplay: ReadonlyArray<ContentDisplayItem>;
}

// Bump when the shape changes so stale localStorage is ignored rather than mis-parsed.
const VERSION = 1;

/**
 * Table preferences (page size, column visibility/order, wrap lines) persisted to localStorage.
 * Stored column ids are reconciled against the current defaults so a renamed/removed column can
 * never blank a table, and newly-added columns appear automatically.
 */
export function useTablePrefs(key: string, defaults: TablePrefs): [TablePrefs, (p: TablePrefs) => void] {
  const storageKey = `fcc.prefs.v${VERSION}.${key}`;

  const [prefs, setPrefs] = useState<TablePrefs>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw) as Partial<TablePrefs>;
      const defIds = new Set(defaults.contentDisplay.map((c) => c.id));
      const stored = (parsed.contentDisplay ?? []).filter((c) => defIds.has(c.id));
      const storedIds = new Set(stored.map((c) => c.id));
      const merged = [...stored, ...defaults.contentDisplay.filter((c) => !storedIds.has(c.id))];
      return {
        pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : defaults.pageSize,
        wrapLines: typeof parsed.wrapLines === 'boolean' ? parsed.wrapLines : defaults.wrapLines,
        contentDisplay: merged.length ? merged : defaults.contentDisplay,
      };
    } catch {
      return defaults;
    }
  });

  const update = useCallback(
    (p: TablePrefs) => {
      setPrefs(p);
      try {
        localStorage.setItem(storageKey, JSON.stringify(p));
      } catch {
        /* private mode / quota — keep in-memory only */
      }
    },
    [storageKey],
  );

  return [prefs, update];
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200];

/** The settings-gear popover wired to {@link useTablePrefs}. Drop into a Table's `preferences` prop. */
export function TablePreferences({
  prefs,
  onChange,
  contentOptions,
  pageSize = true,
  pageSizes = DEFAULT_PAGE_SIZES,
}: {
  prefs: TablePrefs;
  onChange: (p: TablePrefs) => void;
  contentOptions: ReadonlyArray<{ id: string; label: string; alwaysVisible?: boolean }>;
  pageSize?: boolean;
  pageSizes?: ReadonlyArray<number>;
}) {
  return (
    <CollectionPreferences
      title="Preferences"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      preferences={{ pageSize: prefs.pageSize, wrapLines: prefs.wrapLines, contentDisplay: prefs.contentDisplay }}
      pageSizePreference={
        pageSize ? { title: 'Page size', options: pageSizes.map((v) => ({ value: v, label: String(v) })) } : undefined
      }
      wrapLinesPreference={{ label: 'Wrap lines', description: 'Wrap long cell text instead of truncating it.' }}
      contentDisplayPreference={{
        title: 'Columns',
        description: 'Show, hide and reorder columns. Drag to reorder.',
        options: contentOptions.map((o) => ({ id: o.id, label: o.label, alwaysVisible: o.alwaysVisible })),
      }}
      onConfirm={({ detail }) =>
        onChange({
          pageSize: detail.pageSize ?? prefs.pageSize,
          wrapLines: detail.wrapLines ?? prefs.wrapLines,
          contentDisplay: (detail.contentDisplay as ReadonlyArray<ContentDisplayItem>) ?? prefs.contentDisplay,
        })
      }
    />
  );
}
